/**
 * Hook del detalle de una pantalla DOOH (TG-59): config + estado/último contacto.
 * La red vive en `doohApi`; la página orquesta las mutaciones y llama `refresh`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DoohScreen, DoohScreenStatusResponse } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { doohApi } from '../api/doohApi';

export interface UseDoohScreenResult {
  screen: DoohScreen | null;
  status: DoohScreenStatusResponse | null;
  loading: boolean;
  error: string | null;
  /** `true` si el backend respondió 404 (`DOOH_SCREEN_NOT_FOUND`). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la pantalla.';
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function useDoohScreen(screenId: string | undefined): UseDoohScreenResult {
  const [screen, setScreen] = useState<DoohScreen | null>(null);
  const [status, setStatus] = useState<DoohScreenStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!screenId) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    Promise.all([doohApi.getScreen(screenId), doohApi.getScreenStatus(screenId)])
      .then(([screenResponse, statusResponse]) => {
        if (cancelled) return;
        setScreen(screenResponse);
        setStatus(statusResponse);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isNotFound(err)) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [screenId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { screen, status, loading, error, notFound, refresh };
}
